<template>
  <div>
    <div>
      <div>
        <div class="flex gap-2 align-items-end justify-content-between w-5">
          <div class="flex flex-column gap-2">
            <p class="font-medium">Filtrar actuaciones</p>
            <MultiSelect
              :options="optionsActuaciones"
              v-model="filtros.tiposSelected"
              key="value"
              optionLabel="name"
              placeholder="Tipo de Actuación"
              style="border-radius: 6px"
              class="custom-focus flex-grow-1"
              display="chip"
              :disabled="isLoading"
            >
              <template #value="slotProps">
                <template v-if="!slotProps.value || slotProps.value.length === 0">
                  Tipo de Actuación
                </template>
                <div v-if="slotProps.value.length <= 2">
                  <Chip
                    v-for="(item, index) in slotProps.value"
                    :key="index"
                    :label="item.name"
                    class="mr-1"
                    :removable="!isLoading"
                    @remove="quitarTipo(item)"
                  />
                </div>
                <div v-else>
                  <Chip :label="`${slotProps.value.length} seleccionados...`" />
                </div>
              </template>
            </MultiSelect>
          </div>
          <InputText
            v-model="filtros.nroActuacion"
            placeholder="Nro de actuación"
            style="border-radius: 6px"
            class="custom-focus flex-grow-1"
          />
          <div class="flex justify-content-center align-items-end gap-2">
            <div class="flex flex-column align-items-start">
              <label>Fecha Creación</label>
              <Calendar
                placeholder="Desde"
                dateFormat="dd-mm-yy"
                class="w-12rem custom-focus"
                style="border-radius: 6px"
                v-model="filtros.FechaCreacionDesde"
                :maxDate="hoy"
              />
            </div>
            <p>-</p>
            <Calendar
              v-model="filtros.FechaCreacionHasta"
              placeholder="Hasta"
              dateFormat="dd-mm-yy"
              class="w-12rem custom-focus"
              style="border-radius: 6px !important"
              :maxDate="hoy"
            />
          </div>
          <Button
            class="btn btn-primary flex-grow-0 button-no-text"
            @click="filtrarActuaciones"
            :disabled="isLoading"
          >
            <div>
              <Search color="white" :size="20" />
            </div>
            <span>Buscar</span>
          </Button>
          <Button
            class="btn btn-outline-primary flex-grow-0 button-no-text"
            @click="limpiarFiltros"
            :disabled="isLoading"
          >
            <div>
              <BrushCleaning color="#0B5195" :size="20" />
            </div>
            <span>Limpiar</span>
          </Button>
        </div>
      </div>
      <DataTable
        :value="paginatedActuations"
        :loading="showProgressSpinner"
        style="border-radius: 6px; min-height: 600px"
        class="center-screen mt-5 shadow-2"
      >
        <template #empty>
          <div class="emptyMsg flex w-full justify-content-center">
            <span>No hay actuaciones sin conexión</span>
          </div>
        </template>
        <Column header="Fecha de creación">
          <template #body="{ data }">
            {{ generateDate(data.siis_state.fechaDenuncia) }}
          </template>
        </Column>
        <Column field="siis_state.causaNumero" header="Número de Actuación" />
        <Column header="Actuación">
          <template #body="{ data }">
            {{ Object.keys(store.state.sumarioViews[data.siis_state.formType]?.actuationName)[0] }}
          </template>
        </Column>
        <Column header="Carátula/Causa">
          <template #body="{ data }">
            {{ getCausaCaratula(data) }}
          </template>
        </Column>
        <Column header="Estado">
          <template #body="{ data }">
            <div class="w-full flex justify-content-center gap-2">
              <Button
                class="btn btn-primary checkbtn"
                v-tooltip.bottom="'Continuar con la carga'"
                @click="goToEditOfflineActuation(data)"
              >
                <div class="mt-1">
                  <ShieldCheck color="white" :size="20" />
                </div>
                Aceptar
              </Button>
              <Button
                class="btn btn-outline-danger deletebtn"
                v-tooltip.bottom="'Eliminar actuación'"
                @click="deleteConfirmation(data)"
              >
                <div class="mt-1">
                  <CircleX color="#D20F39" :size="20" />
                </div>
                Eliminar
              </Button>
            </div>
          </template>
        </Column>
      </DataTable>
      <div class="flex justify-content-center mt-2" v-if="actuationsOffline.length > 7">
        <ul
          class="flex list-none border-round-xl overflow-hidden gap-2 border-gray px-3 text-custom-gray"
        >
          <li
            class="cursor-pointer px-3 py-2 my-1 border-gray hover:bg-gray-200 border-round-xl"
            @click="prevPage"
          >
            <i class="pi pi-angle-left"></i>
          </li>
          <li
            v-for="page in totalPages"
            :key="page"
            class="cursor-pointer px-3 py-2 my-1 border-gray hover:opacity-20 border-round-xl"
            :class="[page === currentPage ? 'selected' : 'hover:opacity-20']"
            @click="goToPage(page)"
          >
            {{ page }}
          </li>
          <li
            class="cursor-pointer px-3 py-2 my-1 border-gray hover:bg-gray-200 border-round-xl"
            @click="nextPage"
          >
            <i class="pi pi-angle-right"></i>
          </li>
        </ul>
      </div>
    </div>
    <CustomModal
      :title="titleModal"
      :message="message"
      modalType="delete"
      :button-labels="['Eliminar', 'Cancelar']"
      :loading="isSubmitting"
      @confirm="handleDelete"
      @cancel="closeModal(deleteModal)"
      ref="deleteModal"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStoreCompat } from "@/composables/useStoreCompat";
import { useRouterCompat } from "@/composables/useRouterCompat.js";
import actuationsRepository from "../../../dixieDB/repositories/actuationsRepository";
import actuacionesRepository from "../../../dixieDB/app-offline/repositories/actuacionesRepository";
import dateMixin from "@/mixins/dateMixin";
import actuacionesFunctions from "@/mixins/actuaciones/actuacionesFunctions.js";
import { ShieldCheck, CircleX, BrushCleaning, Search } from "lucide-vue";
import MultiSelect from "primevue/multiselect";
import Chip from "primevue/chip";

export default {
  name: "actuacionesOffline",
  components: {
    ShieldCheck,
    CircleX,
    MultiSelect,
    BrushCleaning,
    Search,
    Chip,
  },
  props: {
    tiposActuaciones: Array,
  },
  setup({ tiposActuaciones }) {
    const store = useStoreCompat();
    const { router } = useRouterCompat();

    const actuationsOffline = ref([]);
    const allOfflineActuations = ref([]);
    const showProgressSpinner = ref(true);
    const isLoading = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(7);

    const deleteModal = ref(null);
    const titleModal = ref("");
    const message = ref("");
    const dataToDelete = ref(null);
    const isSubmitting = ref(false);

    const optionsActuaciones = ref(tiposActuaciones);
    const hoy = ref(new Date());

    const filtros = ref({
      tiposSelected: [],
      nroActuacion: "",
      FechaCreacionDesde: "",
      FechaCreacionHasta: "",
    });

    const totalPages = computed(() => Math.ceil(actuationsOffline.value.length / pageSize.value));

    const paginatedActuations = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      const end = start + pageSize.value;
      return actuationsOffline.value.slice(start, end);
    });

    const getActuationsAmount = computed(() => actuationsOffline.value.length);

    const getCausaCaratula = (actuationData) => {
      if (actuationData?.siis_state?.causaCaratula?.Nombre !== undefined) {
        return actuationData.siis_state.causaCaratula.Nombre;
      }
      let causes =
        actuationData.siis_state.listaArticulos.length > 0
          ? actuationData.siis_state.listaArticulos
          : actuationData.siis_state.listaCaratulas;
      let causesText = "";
      for (let i in causes) {
        if (causes[i]?.Numeroarticulo !== undefined) {
          causesText =
            causesText === ""
              ? causes[i].Numeroarticulo + " - " + causes[i].Codigo
              : causesText + ", " + causes[i].Numeroarticulo + " - " + causes[i].Codigo;
        } else {
          causesText = causesText === "" ? causes[i].Nombre : causesText + ", " + causes[i].Nombre;
        }
      }
      return causesText;
    };

    const goToEditOfflineActuation = (actuationData) => {
      const actuacionType = actuacionesFunctions.methods.changeNameInStore(
        Object.keys(store.state.sumarioViews[actuationData.siis_state.formType]?.actuationName)[0]
      );
      store.dispatch("sumario/setSumario", {
        siis_state: {
          ...actuationData.siis_state,
          idOffline: actuationData.id,
          type: actuationData.type,
        },
      });
      router.push("/actuaciones/new/" + actuacionType + "/initial");
    };

    const { generateDate } = dateMixin.methods;
    const deleteConfirmation = (data) => {
      titleModal.value = "Eliminar actuación sin conexión";
      message.value =
        "¿Está seguro que desea Eliminar esta actuación? Esta acción eliminará la actuación del sistema.";
      dataToDelete.value = data;
      openModal(deleteModal);
    };

    const openModal = (modal) => {
      if (modal.value) return modal.value.open();
      else modal.open();
    };

    const closeModal = (modal) => {
      if (modal.value) return modal.value.close();
      else modal.close();
    };

    const handleDelete = async () => {
      try {
        isSubmitting.value = true;
        await actuacionesRepository.delete(dataToDelete.value.id);
        const updatedActuations = await actuationsRepository.getAllActuations(
          actuacionesRepository.getAll()
        );
        actuationsOffline.value = updatedActuations;
        allOfflineActuations.value = updatedActuations;
      } catch (error) {
        console.error("Error al eliminar la actuación:", error);
      } finally {
        isSubmitting.value = false;
        dataToDelete.value = null;
        closeModal(deleteModal);
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) currentPage.value--;
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) currentPage.value++;
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) currentPage.value = page;
    };

    onMounted(async () => {
      showProgressSpinner.value = true;
      isLoading.value = true;
      try {
        const amount = await actuationsRepository.getActuationsAmount(
          actuacionesRepository.getAll()
        );
        if (amount > 0) {
          const actuations = await actuationsRepository.getAllActuations(
            actuacionesRepository.getAll()
          );
          actuationsOffline.value = actuations;
          allOfflineActuations.value = actuations;
        } else {
          actuationsOffline.value = [];
        }
      } catch (error) {
        console.error("Error fetching actuations:", error);
      } finally {
        showProgressSpinner.value = false;
        isLoading.value = false;
      }
    });

    const quitarTipo = (item) => {
      if (isLoading.value) return;
      filtros.value.tiposSelected = filtros.value.tiposSelected.filter(
        (tipo) => tipo.value !== item.value
      );
      filtrarActuaciones();
    };

    const limpiarFiltros = () => {
      showProgressSpinner.value = true;
      isLoading.value = true;
      filtros.value = {
        tiposSelected: [],
        nroActuacion: "",
        FechaCreacionDesde: "",
        FechaCreacionHasta: "",
      };
      actuationsOffline.value = allOfflineActuations.value;
      currentPage.value = 1;
      showProgressSpinner.value = false;
      isLoading.value = false;
    };

    const filtrarActuaciones = () => {
      let filtradas = [...allOfflineActuations.value];
      const { tiposSelected, nroActuacion, FechaCreacionDesde, FechaCreacionHasta } = filtros.value;

      if (tiposSelected.length > 0) {
        const tipos = tiposSelected.map((t) => t.value);
        filtradas = filtradas.filter((act) => {
          const actuacionTipo = Object.keys(
            store.state.sumarioViews[act.siis_state.formType]?.actuationName
          )[0];
          return tipos.some((tipo) => actuacionTipo.toLowerCase().includes(tipo.toLowerCase()));
        });
      }

      if (nroActuacion) {
        const nro = nroActuacion.trim();
        filtradas = filtradas.filter((act) => act.siis_state.causaNumero === nro);
      }

      if (FechaCreacionDesde) {
        const desde = new Date(FechaCreacionDesde).getTime();
        filtradas = filtradas.filter((act) => {
          const fecha = new Date(act.siis_state.fechaDenuncia).getTime();
          return fecha >= desde;
        });
      }

      if (FechaCreacionHasta) {
        const hasta = new Date(FechaCreacionHasta).getTime();
        filtradas = filtradas.filter((act) => {
          const fecha = new Date(act.siis_state.fechaDenuncia).getTime();
          return fecha <= hasta;
        });
      }

      actuationsOffline.value = filtradas;
      currentPage.value = 1;
    };

    return {
      //refs
      actuationsOffline,
      showProgressSpinner,
      isLoading,
      currentPage,
      deleteModal,
      titleModal,
      message,
      isSubmitting,
      filtros,
      optionsActuaciones,

      //computed
      getActuationsAmount,
      hoy,
      store,
      paginatedActuations,
      totalPages,

      //methods
      getCausaCaratula,
      goToEditOfflineActuation,
      generateDate,
      deleteConfirmation,
      prevPage,
      nextPage,
      goToPage,
      openModal,
      closeModal,
      handleDelete,
      quitarTipo,
      limpiarFiltros,
      filtrarActuaciones,
    };
  },
};
</script>

<style scoped>
.btn {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  box-shadow: 0px 1px 3px rgba(166, 175, 195, 0.4);
  transition: 0.2s ease all;
  cursor: pointer;
  font-weight: 500;
}

.btn:hover {
  opacity: 0.9;
}

.btn-primary {
  background-color: #0b5195;
  color: white;
  border: 1px solid #0b5195;
}

.btn-primary:hover {
  background-color: #0b5195;
  color: #f4f4f4;
}

.btn-outline-danger {
  background-color: #fff;
  color: #d20f39;
  border: 1px solid #d20f39;
}

.btn-outline-danger:hover {
  background-color: #fff;
  color: #d20f39;
}

.btn-outline-primary {
  background-color: #fff;
  color: #0b5195;
  border: 1px solid #0b5195;
}

.btn-outline-primary:hover {
  background-color: #f4f4f4;
}

/* === Otros estilos === */
.center-screen {
  margin: 0 auto 1rem auto;
}

.emptyMsg {
  color: #0b5195;
  font-size: 24px;
  font-weight: 700;
}

.border-gray {
  border: 1px solid #dfe4ea;
}

.selected {
  background-color: #3758f9;
  color: white;
}

.text-custom-gray {
  color: #637381;
}

.custom-focus:focus-within {
  border: 1.5px solid #3758f9;
}

>>> .p-calendar .p-inputtext {
  border-radius: 6px !important;
}

@media (min-width: 1280px) and (max-width: 1400px) {
  .button-no-text {
    width: auto;
  }
  .button-no-text span {
    display: none;
  }
}
</style>
